import { useEffect } from 'react'
import { BrowserProvider, type Eip1193Provider } from 'ethers'
import type { SafeTransaction } from '@safe-global/types-kit'
import useAsync from '@safe-global/utils/hooks/useAsync'
import useChainId from '@/hooks/useChainId'
import { useWeb3ReadOnly } from '@/hooks/wallets/web3ReadOnly'
import chains from '@/config/chains'
import { useHasFeature } from '@/hooks/useChains'
import { FEATURES } from '@safe-global/utils/utils/chains'
import { useSigner } from './wallets/useWallet'
import { useSafeSDK } from './coreSDK/safeCoreSDK'
import useIsSafeOwner from './useIsSafeOwner'
import { Errors, logError } from '@/services/exceptions'
import useSafeInfo from './useSafeInfo'
import {
  getEncodedSafeTx,
  GasMultipliers,
  incrementByGasMultiplier,
  getGasLimitForZkSync as getGasLimitForZkSyncUtil,
} from '@safe-global/utils/hooks/coreSDK/gasLimitUtils'
import { validateOasisSignedCall } from '@/utils/sapphire'
import { assertProvider } from '@/utils/helpers'

const useGasLimit = (
  safeTx?: SafeTransaction,
): {
  gasLimit?: bigint
  gasLimitError?: Error
  gasLimitLoading: boolean
} => {
  const safeSDK = useSafeSDK()
  const web3ReadOnly = useWeb3ReadOnly()
  const { safe } = useSafeInfo()
  const safeAddress = safe.address.value
  const threshold = safe.threshold
  const wallet = useSigner()
  const walletAddress = wallet?.address
  const isOwner = useIsSafeOwner()
  const currentChainId = useChainId()
  const hasSafeTxGas = !!safeTx?.data?.safeTxGas
  const isSignedEthCallGasEstimationEnabled = useHasFeature(FEATURES.SIGNED_ETH_CALL_GAS_ESTIMATION)

  const [gasLimit, gasLimitError, gasLimitLoading] = useAsync<bigint | undefined>(async () => {
    if (!safeAddress || !walletAddress || !safeSDK || !web3ReadOnly || !safeTx) return

    const encodedSafeTx = getEncodedSafeTx(
      safeSDK,
      safeTx,
      isOwner ? walletAddress : undefined,
      safeTx.signatures.size < threshold,
    )

    // if we are dealing with zksync and the walletAddress is a Safe, we have to do some magic
    // FIXME a new check to indicate ZKsync chain will be added to the config service and available under Chain
    if (
      (safe.chainId === chains.zksync || safe.chainId === chains.lens) &&
      (await web3ReadOnly.getCode(walletAddress)) !== '0x'
    ) {
      return getGasLimitForZkSyncUtil(web3ReadOnly, safeSDK, safeTx, safe.chainId, safe.address.value)
    }

    let gasLimit = await web3ReadOnly.estimateGas({ to: safeAddress, from: walletAddress, data: encodedSafeTx })

    // Due to a Nethermind bug, increment by 30% when safeTxGas is set (Gnosis Chain only).
    if (currentChainId === chains.gno && hasSafeTxGas) {
      gasLimit = incrementByGasMultiplier(gasLimit, GasMultipliers[chains.gno])
    }

    // On Oasis Sapphire, eth_estimateGas silently swallows reverts (msg.sender = 0x0).
    // We validate via a signed eth_call so Oasis derives msg.sender from the EIP-712
    // signature — the pre-validated Safe sig (v=1, r=owner) then works correctly.
    // Guard: existing sigs + pre-validated marker must reach threshold to avoid GS020.
    if (
      encodedSafeTx &&
      isSignedEthCallGasEstimationEnabled &&
      wallet?.provider &&
      isOwner &&
      safeTx.signatures.size >= threshold - 1
    ) {
      assertProvider(wallet.provider)
      const walletProvider = new BrowserProvider(wallet.provider as Eip1193Provider)
      await validateOasisSignedCall(
        web3ReadOnly,
        walletProvider,
        { from: walletAddress!, to: safeAddress!, data: encodedSafeTx, gasLimit },
        Number(safe.chainId),
      )
    }

    return gasLimit
  }, [
    safeAddress,
    walletAddress,
    safeSDK,
    web3ReadOnly,
    safeTx,
    isOwner,
    currentChainId,
    hasSafeTxGas,
    threshold,
    safe,
    wallet,
    isSignedEthCallGasEstimationEnabled,
  ])

  useEffect(() => {
    if (gasLimitError) {
      logError(Errors._612, gasLimitError.message)
    }
  }, [gasLimitError])

  return { gasLimit, gasLimitError, gasLimitLoading }
}

export default useGasLimit
